<template>
  <TableList
    v-rect-loading="loading"
    row-key="_id"
    class="candidate-status-list"
    default-expand-all
    :title="$t(`${i18nPrefix}.title`)"
    :actionBtn="actionBtn"
    :rowClassName="getRowClassName"
    :listData="causeList"
    :tableColumns="tableColumns"
    :dragSortFunc="dragSortFunc"
    @create="handleCreateCandidateStatus"
  >
    <template #filter>
      <el-select
        v-model="selectedCandidateStatus"
        clearable
        class="candidate-status-filter"
        :placeholder="$t(`${i18nPrefix}.placeholder.status`)"
        @change="handleCandidateStatusChange"
      >
        <el-option
          v-for="(item) in candidateStatusList"
          :key="item.value"
          :label="item.name"
          :value="item.key"
        />
      </el-select>
      <el-input
        v-model="keywords"
        clearable
        class="keywords-filter"
        :placeholder="$t(`${i18nPrefix}.placeholder.keywords`)"
        @input="handleKeywordsChange"
      />
    </template>
    <template #draggable="{row}">
      <div class="drag-btn">
        <i v-if="row.type == DATA_TYPE.CAUSE" class="iconfont iconMore draggable-handle"></i>
      </div>
    </template>
    <template #page-bottom>
      <DialogWrapper
        saveBtnLabelI18nKey="common.btn.confirm"
        closeOnCancel
        :title="deleteDialog.title"
        :loading="deleteDialog.loading"
        :show.sync="deleteDialog.show"
        @save="handleDelete"
      >
      </DialogWrapper>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { mapActions } from 'vuex'
import { addI18nProperty } from '@/i18n'

const DATA_TYPE = {
  CAUSE: 'cause',
  GROUP: 'group'
}

export default {
  components: {
    TableList,
    DialogWrapper
  },
  props: {

  },
  data () {
    return {
      DATA_TYPE,
      loading: false,
      i18nPrefix: 'admin.company.candidateStatusManagement',
      candidateStatusList: [],
      causeList: [],
      selectedCandidateStatus: null,
      keywords: null,
      deleteDialog: {
        show: false,
        loading: false,
        title: null,
        deleteInfo: null
      }
    }
  },
  computed: {
    actionBtn () {
      return {
        edit: {
          show: (row) => {
            return row.type === DATA_TYPE.CAUSE && !row.is_used
          },
          labelI18nKey: 'common.btn.edit',
          onclick: this.handleEditCandidateStatus
        },
        delete: {
          show: (row) => {
            return row.type === DATA_TYPE.CAUSE
          },
          labelI18nKey: 'common.btn.delete',
          onclick: this.handleDeleteCandidateStatus
        }
      }
    },
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'name',
          formatter: (data, row) => {
            return row.type === DATA_TYPE.GROUP ? data : null
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.causeCN`),
          dataKey: 'cause_cn',
          formatter: (data, row) => {
            return row.type === DATA_TYPE.CAUSE ? data : null
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.causeEN`),
          dataKey: 'cause_en',
          formatter: (data, row) => {
            return row.type === DATA_TYPE.CAUSE ? data : null
          }
        },
        {
          label: this.$t('common.label.sortOperate'),
          isDragSort: true,
          slotName: 'draggable',
          width: 50
        }
      ]
      return columns
    },
    flatGroupCandidateList () {
      const flatList = []
      _.forEach(this.causeList, group => {
        flatList.push(group)
        flatList.push(...group.children)
      })
      return flatList
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('candidateStatus', [
      'getCandidateStatusChangeCauseList',
      'getCandidateStatusList',
      'moveCandidateStatusChangeCause',
      'deleteCandidateStatusChangeCause'
    ]),
    async initData () {
      this.loading = true
      try {
        await this.loadCandidateStatus()
        await this.loadCandidateStatusChangeCause()
      } finally {
        this.loading = false
      }
    },
    async loadCandidateStatus () {
      const resp = await this.getCandidateStatusList()
      _.forEach(resp, candidateStatus => {
        addI18nProperty(candidateStatus, 'name', 'name_cn', 'name_en')
      })
      this.candidateStatusList = resp
    },
    async loadCandidateStatusChangeCause () {
      if (!this.loading) {
        this.$eventBus.$emit('showLoading', true)
      }
      try {
        const params = {
          status: this.selectedCandidateStatus,
          keywords: this.keywords
        }
        const resp = await this.getCandidateStatusChangeCauseList(params)
        if (resp) {
          _.forEach(resp, cause => {
            addI18nProperty(cause, 'cause', 'cause_cn', 'cause_en')
          })
          this.causeList = this.formatCause(resp)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    handleCandidateStatusChange () {
      this.loadCandidateStatusChangeCause()
    },
    handleKeywordsChange () {
      if (!this.keywordsSearchDebounce) {
        this.keywordsSearchDebounce = _.debounce(this.loadCandidateStatusChangeCause, 1000)
      }
      this.keywordsSearchDebounce()
    },
    handleCreateCandidateStatus () {
      this.$router.push('/v-admin/candidate-status/management/add')
    },
    handleEditCandidateStatus (row) {
      this.$router.push(`/v-admin/candidate-status/management/edit/${row.id}`)
    },
    handleDeleteCandidateStatus (row) {
      this.deleteDialog.show = true
      this.deleteDialog.deleteInfo = row
      this.deleteDialog.title = this.$t(`${this.i18nPrefix}.message.deleteCandidateStatus`, { status: row._parent.name, cause: row.cause })
    },
    async handleDelete () {
      this.deleteDialog.loading = true
      try {
        const data = {
          causeId: this.deleteDialog.deleteInfo.id
        }
        const resp = await this.deleteCandidateStatusChangeCause(data)
        if (resp && resp === 'success') {
          this.$message.success(this.$t('common.message.optionSuccess'))
        }
      } finally {
        this.deleteDialog.loading = false
      }
      this.deleteDialog.show = false
      this.$eventBus.$emit('showLoading', true)
      try {
        await this.loadCandidateStatusChangeCause()
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    formatCause (causeList) {
      _.forEach(causeList, cause => {
        addI18nProperty(cause, 'cause', 'cause_cn', 'cause_en')
      })
      const groupCause = _.groupBy(causeList, cause => cause.status.key)
      const result = _.map(this.candidateStatusList, candidateStatus => {
        candidateStatus._id = `g_${candidateStatus.id}`
        candidateStatus.type = DATA_TYPE.GROUP
        const causeList = groupCause[candidateStatus.key] || []
        _.forEach(causeList, cause => {
          cause.type = DATA_TYPE.CAUSE
          cause._id = `g_${candidateStatus.id}_c_${cause.id}`
          cause._parent = candidateStatus
        })
        candidateStatus.children = causeList
        return candidateStatus
      })
      if (this.selectedCandidateStatus) {
        return _.filter(result, item => item.key === this.selectedCandidateStatus)
      }
      return result
    },
    dragSortFunc (newIndex, oldIndex, tableSorter) {
      if (newIndex === oldIndex) {
        return
      }
      if (newIndex === 0) {
        this.resetSort(newIndex, oldIndex, tableSorter)
        return
      }
      const changedList = [...this.flatGroupCandidateList]
      const oldData = this.flatGroupCandidateList[oldIndex]
      const newData = this.flatGroupCandidateList[newIndex]
      const oldDataIndex = changedList.findIndex(item => item._id === oldData._id)
      const newDataIndex = changedList.findIndex(item => item._id === newData._id)
      changedList.splice(oldDataIndex, 1)
      changedList.splice(newDataIndex, 0, oldData)
      const beforeData = changedList[newDataIndex - 1]
      const targetGroup = beforeData.type === DATA_TYPE.GROUP ? beforeData : beforeData._parent
      if (targetGroup.key !== oldData._parent.key) {
        this.resetSort(newIndex, oldIndex, tableSorter)
        return
      }
      const order = []
      let isTargetGroup = false
      _.forEach(changedList, cause => {
        if (cause.type === DATA_TYPE.GROUP) {
          isTargetGroup = cause._id === targetGroup._id
        }
        if (cause.type === DATA_TYPE.CAUSE && isTargetGroup) {
          order.push(cause.id)
        }
      })
      this.handleMove(targetGroup, order)
    },
    resetSort (newIndex, oldIndex, tableSorter) {
      const sortItems = tableSorter.toArray()
      const movedItem = sortItems.splice(newIndex, 1)
      sortItems.splice(oldIndex, 0, movedItem[0])
      tableSorter.sort(sortItems)
    },
    async handleMove (targetGroup, orderList) {
      this.$eventBus.$emit('showLoading', true)
      const data = {
        target_candidate_status: targetGroup.key,
        order_list: orderList
      }
      try {
        const resp = await this.moveCandidateStatusChangeCause(data)
        if (resp === 'success') {
          await this.loadCandidateStatusChangeCause()
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    getRowClassName ({ row }) {
      return {
        'group-row': row.type === DATA_TYPE.GROUP,
        [row._id]: true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-status-list {
  .drag-btn {
    .draggable-handle {
      cursor: pointer;
    }
  }

  .group-row {
    .drag-btn {
      visibility: hidden;
    }
  }

  ::v-deep {
    .el-table__body {
      .el-table__row {
        &.group-row {
          background-color: #d1cfcf;

          &:hover {
            .el-table__cell {
              background-color: #d1cfcf;
            }
          }
        }
      }
    }
  }
}
</style>
